/**
 * Googleマップの経路案内を開く（ゴール編集画面の「Googleマップで開く」、docs/08 §224）。
 *
 * ★ https://www.google.com/maps/... を window.open で開くと、スマホでは Googleマップのアプリが
 *   開いたあとに、**空のブラウザ（のタブ）が残る**（利用者の指摘、2026-10-31）。アプリ用のURL（スキーム）を
 *   使えば、ブラウザを経由せずにアプリへ直接渡せる。
 *   - iPhone: `comgooglemaps://`（アプリが無いと開けないので、開けなかったときだけ https を開く）
 *   - Android: `google.navigation:`（地図アプリへの標準の意図。ブラウザを経由しない）
 *   - それ以外（PC）: https のページを新しいタブで開く（空にはならない）
 */

export type NavPlatform = 'ios' | 'android' | 'other';

export function navPlatform(nav: { userAgent: string; platform?: string; maxTouchPoints?: number }): NavPlatform {
  if (/iPhone|iPad|iPod/i.test(nav.userAgent)) return 'ios';
  // iPadOS は Mac を名乗る。タッチがあれば iPad
  if (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1) return 'ios';
  if (/Android/i.test(nav.userAgent)) return 'android';
  return 'other';
}

/** 現在地からこの地点への経路案内を開くURL（アプリ用と、ふつうのページ用） */
export function navigationUrls(lat: number, lon: number): { app: Record<'ios' | 'android', string>; web: string } {
  const to = `${lat.toFixed(6)},${lon.toFixed(6)}`;
  return {
    app: {
      ios: `comgooglemaps://?daddr=${to}&directionsmode=driving`,
      android: `google.navigation:q=${to}&mode=d`,
    },
    web: `https://www.google.com/maps/dir/?api=1&destination=${to}`,
  };
}

/** 経路案内を開く。スマホではアプリへ直接、PCでは新しいタブ */
export function openNavigation(lat: number, lon: number): void {
  const urls = navigationUrls(lat, lon);
  const platform = navPlatform(navigator);
  if (platform === 'other') {
    window.open(urls.web, '_blank', 'noopener');
    return;
  }
  // アプリが開けば、この画面は裏に回る（hidden）。回らなければ（アプリが入っていない）https のページを開く
  let timer: ReturnType<typeof setTimeout> | null = null;
  const onHide = (): void => {
    if (document.visibilityState !== 'hidden') return;
    if (timer) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHide);
  };
  document.addEventListener('visibilitychange', onHide);
  timer = setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    if (document.visibilityState === 'visible') window.open(urls.web, '_blank', 'noopener');
  }, 1500);
  window.location.href = urls.app[platform];
}
